import {action, observable, toJS} from 'mobx'
import $ from 'jquery'
import Cookies from 'js-cookie'

import Status from 'lib/control/Status'

import valueSourceOptionsGroupStorage from '../class/value_source/ValueSourceOptionsGroupStorage'
import valueSourceOptionsGroupsFactory from '../class/value_source/ValueSourceOptionsGroupsFactory'
import ValueSourceField from '../class/value_source/ValueSourceField'
import ExternalValuesSourceGroupMeta from '../class/external_value_meta/ExternalValuesSourceGroupMeta'
import ProfileSettings from '../model/profile/ProfileSettings'
import ProfileDocument from '../model/profile/ProfileDocument'
import Storefront from '../model/wa/Storefront'
import Category from '../model/wa/Category'
import ShopCoupon from '../model/wa/ShopCoupon'
import ShopGift from '../model/wa/ShopGift'
import ShopNPlusM from '../model/wa/ShopNPlusM'
import ShopDiscount from '../model/wa/ShopDiscount'
import ProductType from '../model/wa/ProductType'
import ProductsSet from '../model/wa/ProductsSet'
import Stock from '../model/wa/Stock'
import YmlGenerationController from '../class/yml_generation/YmlGenerationController'
import ProfileSaveResponse from '../model/profile/ProfileSaveResponse'
import {OPTION_MARK} from '../component/control/DependentSelect'
import ProfileSettingsEditState from '../model/form_state/ProfileSettingsEditState'
import ProfileFieldMappingTransfer from '../class/ProfileFieldMappingTransfer'

import * as ValueSourceType from '../constant/ValueSourceType'
import * as UnitMode from '../constant/OfferCustomParamUnitMode'
import profileOfferSettings from "../store/ProfileOfferSettings"
import {FIXED_COMMA_SEPARATED} from "../constant/ValueSourceType"

const getImageSizeLabel = size => {
    const size_parts = size.split('x')

    if (size_parts.length === 1) {
        return `Макс. ( Ширина, Высота ) = ${size} px`
    } else if (size_parts.length === 2) {
        let width = size_parts[0]
        let height = size_parts[1]

        if (width === height) {
            return `Квадратная обрезка: ${width}x${height} px`
        }

        width = width === ''
            ? 'авто'
            : `${width} px`

        height = height === ''
            ? 'авто'
            : `${height} px`

        return `Ширина = ${width}, Высота = ${height}`

    } else {
        return ''
    }
}

export default class ProfileSettingsContext {
    @observable saving_status = Status.NONE

    profiles
    with_profile = false
    /** @type {ProfileSettings} */
    @observable profile
    /** @type {ProfileDocument} */
    @observable profile_document

    /** @type {YmlGenerationController|null} */
    @observable xml_generator = null

    /** @type {ProfileSettingsEditState} */
    @observable form_state = new ProfileSettingsEditState()

    /** @type {Map<String, Storefront>} */
    storefronts = new Map()

    /** @type {Map} */
    features_by_code = new Map()
    features = []

    /** @type {Map<int, object>} */
    yandex_market_categories
    /** @type {Array<Category>} */
    categories
    /** @type {Array} */
    storefront_options
    /** @type {Array} */
    currency_options
    /** @type {Array} */
    root_category_options
    /** @type {Array<ProductsSet>} */
    sets
    /** @type {Array<Stock>} */
    stocks
    /** @type {Array} */
    set_options
    /** @type {Array<ProductType>} */
    product_types
    /** @type {Array} */
    product_type_options
    /** @type {Array<ShopCoupon>} */
    coupons
    /** @type {Array<ShopGift>} */
    gifts
    /** @type {Array<ShopNPlusM>} */
    n_plus_m_promos
    /** @type {Array<ShopDiscount>} */
    discounts
    image_size_options

    php_path
    cli_command_path
    /** @type {String} */
    generate_manual_command
    /** @type {String} */
    generate_by_schedule_command

    /** @type {ValueSourceOptionsGroupsFactory} */
    value_source_options_groups_factory

    plugin_root_url
    shell_user
    is_dev

    current_send = null

    constructor(state) {
        if (!state) {
            return
        }

        const {profiles, with_profile} = state

        this.profiles = profiles
        this.with_profile = with_profile

        if (!with_profile) {
            return
        }

        this.__updateEnv(state)
        this.__updateSettings(state)
    }

    getStorefrontCurrency(storefront_name) {
        return this.storefronts.has(storefront_name)
            ? this.storefronts.get(storefront_name).currency
            : ''
    }

    getGenerateProfileManualCliCommand() {
        return `${this.php_path} ${this.cli_command_path} ${this.generate_manual_command}`
    }

    getGenerateProfileByScheduleCliCommand() {
        return `* * * * * ${this.php_path} ${this.cli_command_path} ${this.generate_by_schedule_command}`
    }

    @action changeProfileStorefront(storefront_name) {
        this.profile.route_storefront = storefront_name
        this.profile.catalog_selection_min_exported_price = ''
        this.profile.catalog_selection_min_exported_price_currency = this.getStorefrontCurrency(storefront_name)
    }

    @action
    async saveProfile() {
        if (this.saving_status !== Status.NONE) {
            return
        }

        this.saving_status = Status.LOADING
        this.form_state.lockInputs()

        const profile_raw = this._getRawProfileSettings()
        const save_request_data = {
            profile_json: JSON.stringify(profile_raw),
            _csrf: Cookies.get('_csrf'),
        }

        try {
            const response_raw = await $.ajax({
                url: '?plugin=market&module=profileSettings&action=saveProfile',
                method: 'POST',
                dataType: 'json',
                data: save_request_data,
            })

            this.form_state.unlockInputs()

            const response = new ProfileSaveResponse(response_raw)

            if (response.success) {
                this.handleSaveProfileResponse(response)

                return true
            } else {
                console.warn('error')

                this.resetSavingStatus(Status.ERROR)

                return false
            }
        } catch (e) {
            console.warn(e)

            this.form_state.unlockInputs()
            this.resetSavingStatus(Status.ERROR)

            return false
        }
    }

    @action changeProfileOfferType(new_offer_type) {
        const old_offer_type = this.profile.offer_offer_type

        this.__copyProfileOfferTypeFieldMapping(old_offer_type, new_offer_type)

        this.profile.offer_offer_type = new_offer_type
    }

    /**
     * @param {OfferFieldValueSource} value_source
     * @return {boolean}
     */
    isWithUnit(value_source) {
        if (value_source.source_type !== ValueSourceType.PRODUCT_FEATURE) {
            return false
        }

        if (!this.features_by_code.has(value_source.source_value)) {
            return false
        }

        const feature = this.features_by_code.get(value_source.source_value)

        return feature.is_feature_with_unit
    }

    isFeatureWithUnit(feature) {
        const type_parts = feature.type.split('.')

        return (type_parts.length === 2 && type_parts[0] === 'range')
            || (type_parts.length === 2 && type_parts[0] === 'dimension')
            || (type_parts.length === 3 && type_parts[1] === 'dimension')
    }

    /**
     * @param {OfferFieldValueSource} value_source
     * @return {String|null}
     */
    getFeatureName(value_source) {
        if (value_source.source_type !== ValueSourceType.PRODUCT_FEATURE) {
            return null
        }

        if (!this.features_by_code.has(value_source.source_value)) {
            return null
        }

        return this.features_by_code.get(value_source.source_value).name
    }

    getYandexCategoryName(category_id) {
        return this.yandex_market_categories.has(category_id)
            ? this.yandex_market_categories.get(category_id).name
            : ''
    }

    _getRawProfileSettings() {
        const profile_raw = toJS(this.profile)

        delete profile_raw.catalog_selection
        profile_raw.catalog_selection_mode = this.profile.catalog_selection.selection_mode
        profile_raw.catalog_selection_entities_union = this.profile.catalog_selection.catalog_selection_entities_union.map(entity => ({
            type: entity.type,
            ids: [...entity.ids],
        }))

        const deleteUniqueKey = obj => {
            delete obj._unique_key

            return obj
        }

        profile_raw.delivery_delivery_options = profile_raw.delivery_delivery_options.map(deleteUniqueKey)
        profile_raw.delivery_pickup_options = profile_raw.delivery_pickup_options.map(deleteUniqueKey)
        profile_raw.offer_field_mapping_custom_params = profile_raw.offer_field_mapping_custom_params.map(deleteUniqueKey)
        profile_raw.promo_coupons = toJS(Array.from(this.profile.promo_coupons.values()))
        profile_raw.promo_gifts = toJS(Array.from(this.profile.promo_gifts.values()))
        profile_raw.promo_n_plus_m_promos = toJS(Array.from(this.profile.promo_n_plus_m_promos.values()))
        profile_raw.promo_discounts = toJS(Array.from(this.profile.promo_discounts.values()))
        profile_raw.offer = profileOfferSettings.getFieldsValues()
        profile_raw.category_yandex_category_mapping = Array.from(this.profile.category_yandex_category_mapping.entries()).map(entry => ({
            key: entry[0],
            value: entry[1]
        }))

        return profile_raw
    }

    /**
     * @param {ProfileSaveResponse} response
     */
    handleSaveProfileResponse(response) {
        this.updateProfileNameInTab(this.profile.id, this.profile.name)

        this.__updateSettings(response.state)

        this.resetSavingStatus(Status.SUCCESS)
    }

    updateProfileNameInTab(profile_id, new_name) {
        const $tab_link = $('#s-importexport-profile').find(`a[href="#/market:${profile_id}/"]`)

        if ($tab_link.length === 1) {
            $tab_link.text(new_name)
        }
    }

    @action openParamsByFeaturesBuilder() {
        this.form_state.openParamsByFeaturesBuilder()
    }

    @action closeParamsByFeaturesBuilder() {
        this.form_state.closeParamsByFeaturesBuilder()
    }

    @action toggleParamsByFeaturesBuilderFeature(feature_code, toggle) {
        this.form_state.toggleParamsByFeaturesBuilderFeature(feature_code, toggle)
    }

    @action toggleAllParamsByFeaturesBuilderFeatures(toggle) {
        Array.from(this.features_by_code.keys()).forEach(
            feature_code => this.form_state.toggleParamsByFeaturesBuilderFeature(feature_code, toggle)
        )
    }

    @action addNewParamsByFeaturesBuilder(addCustomParam) {
        this.form_state.getParamsBuilderSelectedFeatureCodes().forEach(feature_code => {
            const feature = this.features_by_code.get(feature_code)

            const custom_param_params = {
                value_source: {
                    source_type: ValueSourceType.PRODUCT_FEATURE,
                    source_value: feature_code,
                },
                name: feature.name,
                unit_mode: feature.is_feature_with_unit ? UnitMode.FROM_VALUE : UnitMode.CUSTOM,
                custom_unit: '',
            }

            addCustomParam(custom_param_params)
        })

        this.closeParamsByFeaturesBuilder()
    }

    isParamsByFeaturesBuilderOpened() {
        return this.form_state.isParamsByFeaturesBuilderOpened()
    }

    isParamsByFeaturesBuilderFeatureSelected(feature_code) {
        return this.form_state.isParamsByFeaturesBuilderFeatureSelected(feature_code)
    }

    async runXmlGeneration() {
        this.xml_generator = new YmlGenerationController(this.profile.id)

        const save_result = await this.saveProfile()
        if (!save_result) {
            return
        }

        this.form_state.lockInputs()

        try {
            await this.xml_generator.sendInitializationRequest()
        } catch (e) {
            console.warn(e)

            this.form_state.unlockInputs()

            this.xml_generator = null
            this.resetSavingStatus(Status.ERROR)

            return
        }

        while (!this.xml_generator.isCompleted()) {
            await this.xml_generator.trySendInfoRequest()
        }

        this.form_state.unlockInputs()

        if (this.xml_generator.profile_document) {
            this.profile_document = new ProfileDocument(this.xml_generator.profile_document)
        }
    }

    resetSavingStatus(status) {
        this.saving_status = status

        setTimeout(() => {
            this.saving_status = Status.NONE
        }, 2000)
    }

    getUrl(plugin_path) {
        return this.plugin_root_url + (plugin_path || '')
    }

    fetchYandexCategoryOptions(search) {
        if (search.length < 3) {
            return new Promise(resolve => resolve([]))
        }

        const deferred = $.Deferred()

        if (this.current_send) {
            this.current_send.abort()
        }
        this.current_send = null

        $.ajax({
            url: '?plugin=market&module=yandexCategories&action=getOptions',
            method: 'GET',
            dataType: 'json',
            data: {search: search},
            beforeSend: xhr => {
                this.current_send = xhr
            }
        }).then((response) => {
            this.current_send = null

            return response && response.data && response.data.success
                ? response.data.options
                : []
        }, () => {
            this.current_send = null
        }).then(deferred.resolve, deferred.reject)


        return deferred.promise()
    }

    lazyGetValueSourceOptions(field, _search, value_source) {
        return new Promise(resolve => {
            const options = []

            let search = ''
            if (_search) {
                search = _search.toLowerCase()
            }

            const options_groups = valueSourceOptionsGroupsFactory.getForDataset(field)

            for (const group of options_groups) {
                const is_empty_group = group.isEmpty() || group.isDefault()

                if (!search && is_empty_group) {
                    options.push({
                        is_empty: true,
                        label: group.group_label,
                        value: group.group_source_type,
                    })
                }

                if (!is_empty_group) {
                    const group_options = group.options
                        .filter(o => !search || o.label.toLowerCase().includes(search))
                        .map(o => ({
                            is_empty: false,
                            label: o.label,
                            value: `${group.group_source_type}/${o.value}`,
                        }))

                    if (group_options.length > 0) {
                        options.push({
                            is_empty: false,
                            label: group.group_label,
                            options: group_options,
                        })
                    }
                }
            }

            if (value_source.source_type === FIXED_COMMA_SEPARATED) {
                if (value_source.source_value.split(',').length > 1) {
                    options.push({
                        label: value_source.source_value,
                        value: value_source.source_value
                    })
                }
            }
            resolve(options)
        })
    }

    @action __updateEnv(state) {
        const {
            storefronts,
            currency_codes,
            yandex_market_categories,
            categories,
            root_categories,
            sets,
            stocks,
            product_types,
            coupons,
            gifts,
            n_plus_m_promos,
            discounts,
            image_sizes,
            product_fields,
            product_id_fields,
            features,
            price_fields,
            external_values_source_groups,
            php_path,
            cli_command_path,
            generate_manual_command,
            generate_by_schedule_command,
            plugin_root_url,
            shell_user,
            is_dev
        } = state

        /** @type {Array<Storefront>} */
        const storefronts_obj = storefronts.map(s => new Storefront(s))

        this.storefronts = new Map(storefronts_obj.map(s => [
            s.storefront,
            s
        ]))

        this.storefront_options = storefronts_obj.map(s => ({
            label: s.storefront,
            value: s.storefront,
        }))
        this.storefront_options.unshift({
            label: '',
            value: '',
        })


        this.currency_options = currency_codes.map(code => ({
            label: code,
            value: code,
        }))
        this.currency_options.unshift({
            label: 'По-умолчанию для витрины',
            value: '',
        })


        this.yandex_market_categories = new Map(yandex_market_categories.map(c => ([
            parseInt(c.id),
            c
        ])))


        this.categories = categories.map(c => new Category(c))


        this.root_category_options = root_categories.map(category => ({
            label: category.name,
            value: category.id,
            mark: category.state == '0' ? OPTION_MARK.HIDDEN : '',
        }))


        this.sets = sets.map(set => new ProductsSet(set))

        this.set_options = sets.map(set => ({
            label: set.name,
            value: set.id,
        }))

        this.stocks = stocks.map(stock => new Stock(stock))

        this.product_types = product_types.map(pt => new ProductType(pt))

        this.product_type_options = product_types.map(type => ({
            label: type.name,
            value: type.id,
        }))


        this.coupons = coupons.map(c => new ShopCoupon(c))
        this.gifts = gifts.map(g => new ShopGift(g))
        this.n_plus_m_promos = n_plus_m_promos.map(npm => new ShopNPlusM(npm))
        this.discounts = discounts.map(d => new ShopDiscount(d))

        this.image_size_options = image_sizes.map(size => ({
            label: getImageSizeLabel(size),
            value: size,
        }))


        this.features_by_code = new Map()
        features.forEach(feature => {
            feature.is_feature_with_unit = this.isFeatureWithUnit(feature)

            this.features_by_code.set(feature.code, feature)
            this.features.push(feature)
        })


        this.php_path = php_path
        this.cli_command_path = cli_command_path
        this.generate_manual_command = generate_manual_command
        this.generate_by_schedule_command = generate_by_schedule_command

        const product_source_fields = product_fields.map(f => new ValueSourceField(f))
        const product_id_source_fields = product_id_fields.map(f => new ValueSourceField(f))
        const feature_source_fields = features.map(feature => new ValueSourceField({
            field: feature.code,
            label: feature.name,
        }))
        const price_source_fields = price_fields.map(f => new ValueSourceField(f))

        valueSourceOptionsGroupStorage.init(
            product_source_fields,
            product_id_source_fields,
            feature_source_fields,
            price_source_fields,
            external_values_source_groups.map(g => new ExternalValuesSourceGroupMeta(g)),
            currency_codes,
            this.image_size_options
        )

        this.plugin_root_url = plugin_root_url
        this.shell_user = shell_user
        this.is_dev = is_dev
    }

    @action __updateSettings(new_state) {
        const {
            profile_settings, profile_document,
        } = new_state

        this.profile = new ProfileSettings(profile_settings)

        profileOfferSettings.init(profile_settings.offer)

        this.profile_document = new ProfileDocument(profile_document)
    }

    @action __copyProfileOfferTypeFieldMapping(source_type, destination_type) {
        const transfer = new ProfileFieldMappingTransfer()

        transfer.cloneSettings(this.profile, source_type, destination_type)
    }
}